import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  effect,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as go from 'gojs';

import { MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import {
  GetEnumValuesDto,
  TextParserFormulaModel,
  TextParserThenConditionType,
} from 'src/app/core/api/models';
import { EnumToTextConvertorPipe } from 'src/app/core/pipes/enum-text.pipe';
import { AppStateService } from 'src/app/core/services/app-state.service';
import {
  DialogService,
  confirmDialogConfig,
} from 'src/app/core/services/dialog.service';
import { AppThemeMode } from 'src/app/core/types/app-theme-mode.type';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'diagram',
  templateUrl: './diagram.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
})
export class DiagramComponent {
  //#region properties
  enumToTextConvertorPipe = inject(EnumToTextConvertorPipe);
  cdr = inject(ChangeDetectorRef);
  appStateService = inject(AppStateService);
  dialogService = inject(DialogService);
  translate = inject(TranslateService);

  protected onDestroy$ = new Subject<void>();

  @Input() formulas: Array<TextParserFormulaModel> = [];
  @Input() ifConditionValue: string = '';
  @Input() textParserIfConditionTypeEnumValues: GetEnumValuesDto[] = [];
  @Input() textParserThenConditionTypeEnumValues: GetEnumValuesDto[] = [];
  @Input() diagramId: string = 'diagram';

  @Output() selectedNode = new EventEmitter<string>();
  @Output() newNode = new EventEmitter<any>();
  @Output() removeNode = new EventEmitter<string>();
  currentTheme: AppThemeMode = 'light';

  constructor() {
    effect(() => {
      if (this.appStateService.currentThemeMode()) {
        this.diagram.themeManager.currentTheme =
          this.appStateService.currentThemeMode();

        this.currentTheme = this.appStateService.currentThemeMode();

        this.reload();
      }
    });
  }

  diagram!: go.Diagram;
  //#endregion

  ngOnInit() {
    this.configGraph();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formulas']) this.cdr.detectChanges(); // Force change detection

    //used after refresh tab in browser and get enums
    if (
      changes['textParserThenConditionTypeEnumValues'] &&
      changes['textParserThenConditionTypeEnumValues'].currentValue
    )
      this.setDataToDiagram();
  }

  configGraph() {
    this.configBaseDiagram();
    this.initializeDiagramListeners();
    this.setDataToDiagram();

    // set up some colors/fonts for the default ('light') and dark Themes
    this.setTheme();

    // define the Node template
    this.configNodes();

    //menu that shows when RC on node
    // this.addContextMenu();

    // define the Link template
    this.setLinkTemplate();
  }

  //#region Initialize Diagram Event Listeners
  initializeDiagramListeners(): void {
    this.diagram.addDiagramListener('ObjectSingleClicked', (e) => {
      const node = e.subject.part as go.Node;
      if (node) {
        this.selectedNode.emit(node.data?.key);
      }
    });
  }
  //#endregion

  //#region Node config
  configNodes() {
    // Used to convert the node's tree level into a theme color

    this.diagram.nodeTemplate = new go.Node(go.Panel.Spot, {
      selectionObjectName: 'BODY',
    })
      .add(
        new go.Panel(go.Panel.Auto, { name: 'BODY' }).add(
          // define the node's outer shape
          new go.Shape('RoundedRectangle', {
            name: 'SHAPE',
            strokeWidth: 0,
            portId: '',
            spot1: go.Spot.TopLeft,
            spot2: go.Spot.BottomRight,
          }).theme('fill', 'background'),
          new go.Panel(go.Panel.Table, {
            margin: 0.5,
            defaultRowSeparatorStrokeWidth: 0.5,
          })
            .theme('defaultRowSeparatorStroke', 'divider')
            .add(
              new go.Panel(go.Panel.Table, {
                padding: new go.Margin(18, 18, 18, 24),
              })
                .addColumnDefinition(0, { width: 240 })
                .add(
                  new go.Panel(go.Panel.Table, {
                    column: 0,
                    alignment: go.Spot.Right,
                    stretch: go.Stretch.Vertical,
                    defaultAlignment: go.Spot.Right,
                  }).add(
                    new go.Panel(go.Panel.Horizontal, { row: 0 }).add(
                      new go.Panel(go.Panel.Auto, {
                        width: 200,
                        margin: 5,
                      }).add(
                        new go.Panel(go.Panel.Auto).add(
                          new go.TextBlock({
                            editable: false,
                            font: 'bold 14px Vazirmatn',
                          })
                            .bind('text', '', () =>
                              this.translate.instant('common.if')
                            )
                            .theme('stroke', 'text')
                        ),
                        new go.TextBlock(this.translate.instant('common.if'), {
                          editable: false,
                        })
                          .bindTwoWay(
                            'text',
                            'ifConditionType',
                            (type) => type || '-'
                          )
                          .theme('stroke', 'text')
                      )
                    ),

                    new go.Panel(go.Panel.Horizontal, { row: 1 }).add(
                      new go.Panel(go.Panel.Auto, {
                        width: 230,
                        margin: 5,
                      }).add(
                        new go.Panel(go.Panel.Auto).add(
                          new go.TextBlock({
                            editable: false,
                            font: 'bold 14px Vazirmatn',
                            margin: new go.Margin(3, 0, 0, 0),
                          })
                            .bind('text', '', () =>
                              this.translate.instant('common.ifValue')
                            )
                            .theme('stroke', 'text')
                        ),
                        new go.TextBlock({
                          minSize: new go.Size(10, 12),
                          margin: new go.Margin(2, -1),
                          maxSize: new go.Size(150, 150),
                          font: 'bold 14px Vazirmatn',
                        })
                          .theme('stroke', 'badgeText')
                          .bindTwoWay('text', 'ifConditionValue')
                      )
                    ),
                    new go.Panel(go.Panel.Horizontal, { row: 2 }).add(
                      new go.Panel(go.Panel.Auto, {
                        width: 200,
                        margin: 5,
                      }).add(
                        new go.Panel(go.Panel.Auto).add(
                          new go.TextBlock({
                            editable: false,
                            font: 'bold 14px Vazirmatn',
                          })
                            .bind('text', '', () =>
                              this.translate.instant('common.then')
                            )
                            .theme('stroke', 'text')
                        ),
                        new go.TextBlock({
                          editable: false,
                        })
                          .bindTwoWay('text', 'thenConditionType')
                          .theme('stroke', 'text')
                      )
                    )
                  )
                ),
              new go.Panel(go.Panel.Table, {
                row: 1,
                defaultColumnSeparatorStrokeWidth: 0.5,
              })
                .theme('defaultColumnSeparatorStroke', 'divider')
                .add(
                  this.addAndRemoveNodesBtn('Add'),
                  this.addAndRemoveNodesBtn('Delete')
                )
            )
        ), // end Auto Panel
        new go.Shape('RoundedLeftRectangle', {
          alignment: go.Spot.Left,
          alignmentFocus: go.Spot.Left,
          stretch: go.Stretch.Vertical,
          width: 6,
          strokeWidth: 0,
        }).themeObject('fill', '', 'levels', this.findLevelColor),
        go.GraphObject.build('TreeExpanderButton', {
          _treeExpandedFigure: 'LineUp',
          _treeCollapsedFigure: 'LineDown',
          name: 'BUTTONX',
          alignment: go.Spot.Bottom,
          opacity: 0, // initially not visible
        })
          // button is visible either when node is selected or on mouse-over
          .bindObject('opacity', 'isSelected', (s) => (s ? 1 : 0))
      )
      // for sorting, have the Node.text be the data.name
      .bind('text', 'ifConditionValue')
      // bind the Part.layerName to control the Node's layer depending on whether it isSelected
      .bindObject('layerName', 'isSelected', (sel) => (sel ? 'Foreground' : ''))
      .bindTwoWay('isTreeExpanded');
  }

  addNewNode(node: go.Node | null = null, includeParent: boolean = true): void {
    if (!node?.data && includeParent) return; // جلوگیری از خطا در صورت مقدار نداشتن node
    let parentNodeData: NodeData | undefined;
    let newNodeData: NodeData;

    const newNodeId = uuidv4();

    this.diagram.model.commit((model) => {
      if (includeParent) {
        parentNodeData = node.data as NodeData;
        newNodeData = {
          ifConditionValue: '-',
          thenConditionType: '-',
          ifConditionType: parentNodeData.ifConditionType,
          parent: parentNodeData.key,
          key: newNodeId,
        };
      } else {
        newNodeData = {
          ifConditionValue: '-',
          thenConditionType: '-',
          ifConditionType: '-',
          key: newNodeId,
        };
      }

      model.addNodeData(newNodeData);
      const newDiagramNode = this.diagram.findNodeForData(newNodeData);

      if (newDiagramNode) {
        if (includeParent) newDiagramNode.location = node.location;
        else newDiagramNode.location = new go.Point(0, 0);

        this.diagram.commandHandler.scrollToPart(newDiagramNode);
        this.diagram.select(newDiagramNode);

        // انتشار رویدادها
        this.selectedNode.emit(newNodeId);
        this.newNode.emit({
          id: newNodeId,
          parentId: includeParent ? parentNodeData?.key : null,
        });
      }
    }, 'add node');
  }

  removeSelectedNode(node: go.Node) {
    const dialogResult = this.getConfirmDialog({
      isConfirmDelete: true,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          // remove the whole subtree, including the node itself
          if (node !== null) {
            this.diagram.commit(
              (d) => d.removeParts(node.findTreeParts()),
              'remove dept'
            );
          }
          this.removeNode.emit(node?.data?.key);
        }
      });
  }

  addAndRemoveNodesBtn(name: string) {
    // Check if the button is 'Add' or 'Remove'
    const isAdd = name === 'Add';

    return new go.Panel(go.Panel.Table, {
      // Handle mouse hover effects
      mouseEnter: (e, obj: any) =>
        (obj.background = this.currentTheme == 'light' ? '#eaecf9' : '#11183e'),
      mouseLeave: (e, obj: any) => (obj.background = 'transparent'),
      name,
      background: 'transparent',
      cursor: 'Pointer',
      column: isAdd ? 0 : 1,
      width: 170,
      height: 40,
    })
      .bind('visible', 'thenConditionTypeEnum', (value) => {
        // Make visible only if `thenConditionTypeEnum` is SendSmsToSenderAgent and isAdd
        if (isAdd)
          return value == TextParserThenConditionType.SendSmsToSenderAgent;
        else return true;
      })
      .bind('width', 'thenConditionTypeEnum', (value) => {
        if (value != TextParserThenConditionType.SendSmsToSenderAgent)
          return 300;
        else return 170;
      })
      .add(
        new go.Panel(go.Panel.Horizontal).add(
          new go.Picture({
            source: isAdd
              ? '/assets/media/icons/duotune/general/add-svgrepo-com.svg'
              : '/assets/media/icons/duotune/general/trash-bin-trash-svgrepo-com.svg',
            desiredSize: new go.Size(25, 25),
          })
            .bind('filter', '', () => {
              if (this.currentTheme == 'light') return 'none';
              else return 'invert(1) brightness(2)';
            })
            .theme('fill', 'text')
            .themeData('fill', name, null),
          new go.TextBlock(
            isAdd
              ? this.translate.instant('common.add')
              : this.translate.instant('common.delete')
          )
            .theme('stroke', 'text')
            .themeData('stroke', name, null)
            .theme('font', 'link')
        )
      )
      .set({
        cursor: 'pointer',
        click: (e, obj: any) => {
          // Add or remove node based on button type
          if (isAdd) this.addNewNode(obj.part);
          else this.removeSelectedNode(obj.part);
        },
      });
  }

  findLevelColor(node: go.Node) {
    return node.findTreeLevel();
  }
  //#endregion

  //#region Configure Base Diagram
  configBaseDiagram() {
    this.diagram = new go.Diagram(this.diagramId, {
      allowCopy: false,
      allowDelete: false,
      initialAutoScale: go.AutoScale.UniformToFill,
      maxSelectionCount: 1, // Allow selecting only one node
      validCycle: go.CycleMode.DestinationTree, // Restrict to valid tree structures

      'clickCreatingTool.isEnabled': false, // Disable default click-to-create behavior
      doubleClick: (e) => {
        e.diagram.currentTool.doCancel(); // Prevent any action on double-click
      },

      // Tree layout settings
      layout: new go.TreeLayout({
        treeStyle: go.TreeStyle.LastParents,
        arrangement: go.TreeArrangement.Horizontal,
        angle: 90,
        layerSpacing: 35,
        alternateAngle: 90,
        alternateLayerSpacing: 35,
        alternateAlignment: go.TreeAlignment.Bus,
        alternateNodeSpacing: 20,
      }),

      'undoManager.isEnabled': true, // Enable undo/redo
      'themeManager.changesDivBackground': true, // Apply theme settings
    });
  }
  //#endregion

  setDataToDiagram() {
    let formulas = this.formulas.map((item) => {
      const transformedItem: any = {
        key: item.id,
        thenConditionTypeEnum: item?.thenConditionType,
        ifConditionValue: item.ifConditionValue || '-',
        thenConditionType:
          this.enumToTextConvertorPipe.transform(
            item?.thenConditionType,
            this.textParserThenConditionTypeEnumValues
          ) || '-',
        ifConditionType:
          this.enumToTextConvertorPipe.transform(
            item?.ifConditionType,
            this.textParserIfConditionTypeEnumValues
          ) || '-',
      };

      if (
        item.previousFormulaId !== null &&
        item.previousFormulaId !== undefined
      ) {
        transformedItem.parent = item.previousFormulaId;
      }

      return transformedItem;
    });

    this.diagram.model = new go.TreeModel(formulas);
  }

  setLinkTemplate() {
    this.diagram.linkTemplate = new go.Link({
      routing: go.Routing.AvoidsNodes,
      layerName: 'Background',
      corner: 5,
    }).add(new go.Shape({ strokeWidth: 2 }).theme('stroke', 'link')); // the link shape
  }

  setTheme() {
    this.diagram.themeManager.set('light', {
      colors: {
        background: '#fff',
        text: '#111827',
        subtext: '#6b7280',
        badge: '#eaecf9',
        badgeBorder: '#1c2767',
        badgeText: '#1c2767',
        divider: '#6b7280',
        btnHover: '',
        tooltip: '#1f2937',
        levels: [
          '#AC193D',
          '#2672EC',
          '#8C0095',
          '#5133AB',
          '#008299',
          '#D24726',
          '#008A00',
          '#094AB2',
        ],
        link: '#9ca3af',
        div: '#f3f4f6',
      },
      fonts: {
        name: '500 1.2rem InterVariable, Vazirmatn',
        normal: '0.875rem InterVariable, Vazirmatn',
        badge: '500 0.75rem InterVariable, Vazirmatn',
        link: '600 0.875rem InterVariable, Vazirmatn',
      },
    });

    this.diagram.themeManager.set('dark', {
      colors: {
        background: '#1a1a27',
        text: '#fff',
        subtext: '#d1d5db',
        badge: '#172052',
        badgeBorder: '#d4d9f3',
        badgeText: '#d4d9f3',
        btnHover: '#3d3d58',
        link: '#6b7280',
        div: '#262638',
      },
    });
  }

  //#region Context Menu Handling Logic

  //#region Add Context Menu to Node Template
  addContextMenu() {
    this.diagram.nodeTemplate.contextMenu = go.GraphObject.build('ContextMenu')
      .add(this.createDeleteNodeButton())
      .add(this.createDeleteNodeWithChildrenButton());
  }
  //#endregion

  //#region Create 'Delete Node' Button
  createDeleteNodeButton() {
    return go.GraphObject.build('ContextMenuButton', {
      click: (e, button: any) => this.handleDeleteNode(button),
    }).add(
      new go.TextBlock(this.translate.instant('common.singleDelete'), {
        margin: new go.Margin(4, 20),
        font: '14px Vazirmatn',
      })
    );
  }
  //#endregion

  //#region Create 'Delete Node with Children' Button
  createDeleteNodeWithChildrenButton() {
    return go.GraphObject.build('ContextMenuButton', {
      click: (e, button: any) => this.handleDeleteNodeWithChildren(button),
    }).add(
      new go.TextBlock(this.translate.instant('common.deleteWithChildren'), {
        margin: new go.Margin(4, 20),
        font: '14px Vazirmatn',
      })
    );
  }
  //#endregion

  //#region Handle 'Delete Node' Action

  handleDeleteNode(button: any) {
    const dialogResult = this.getConfirmDialog({
      isConfirmDelete: true,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          const node = button.part.adornedPart;
          if (node) {
            this.diagram.model.commit((m: any) => {
              const children = node.findTreeChildrenNodes();
              while (children.next()) {
                const child = children.value;
                m.setParentKeyForNodeData(
                  child.data,
                  node.findTreeParentNode().data.key
                );
              }
              m.removeNodeData(node.data);
            }, 'reparent remove');
            this.removeNode.emit(node?.data?.key);
          }
        }
      });
  }
  //#endregion

  //#region Handle 'Delete Node with Children' Action
  handleDeleteNodeWithChildren(button: any) {
    const dialogResult = this.getConfirmDialog({
      isConfirmDelete: true,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          const node = button.part.adornedPart;
          this.removeNode.emit(node?.data?.key);

          if (node) {
            this.diagram.commit(
              (d) => d.removeParts(node.findTreeParts()),
              'remove dept'
            );
          }
        }
      });
  }
  //#endregion

  //#endregion

  //#region Zoom and Center Controls
  setZoomToFit() {
    this.diagram.commandHandler.zoomToFit();
  }

  increaseZoom() {
    this.diagram.commandHandler.increaseZoom();
  }

  decreaseZoom() {
    this.diagram.commandHandler.decreaseZoom();
  }

  undoDiagramChanges() {
    if (this.diagram.undoManager.canUndo()) this.diagram.undoManager.undo();
  }

  redoDiagramChanges() {
    if (this.diagram.undoManager.canRedo()) this.diagram.undoManager.redo();
  }
  //#endregion

  reload() {
    this.setDataToDiagram();

    this.diagram.model = go.Model.fromJson(this.diagram.model.toJson());
    // make sure new data keys are unique positive integers
    let key = 1;
    this.diagram.model.makeUniqueKeyFunction = (model, data) => {
      let k = data.key || key;
      while (model.findNodeDataForKey(k)) k++;
      data.key = key = k;
      return k;
    };
  }

  getConfirmDialog(
    config: confirmDialogConfig
  ): MatDialogRef<ConfirmDialogComponent, any> {
    return this.dialogService.confirmDialog(config);
  }

  unSubs() {
    this.onDestroy$.next();
  }

  ngOnDestroy() {
    this.unSubs();
    this.onDestroy$.complete();
  }
}

interface NodeData {
  ifConditionValue?: string;
  thenConditionType?: string;
  ifConditionType?: any;
  key?: string;
  parent?: string;
}
